
import React, { useState, useEffect } from 'react'
import * as XLSX from 'xlsx';
import axios from 'axios';
import {baseUrl, limit} from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { useNavigate, Link } from 'react-router-dom';
import ColumnFilterRow from "../../../components/Filter/ColumnFilterRow";
import { isFilterActive, matchFilterText, patchFilter } from "../../../components/Filter/columnFilter";
import Loader from "../../../components/Loader";
// import Pagination from "react-js-pagination";

// The table's columns, in order, with the type that decides which operators its filter offers:
// text columns get contains / equals to / starts with / ends with, figures get the numeric set,
// and Status gets an All / Active / Inactive picker. text(item) is what the filter matches on,
// which is the cell's own displayed value -- so filtering and reading agree.
const COLS = [
    { key: 'name',          label: 'Vessel Name',          type: 'text',   text: (i) => i.name },
    // The category's name, joined on by the list endpoint. Blank on a vessel that has none.
    { key: 'vessel_category', label: 'Vessel Category',    type: 'text',   text: (i) => i.vessel_category_name },
    // Empty Weight sits here, between the category and the measurements, as the spec asks and as
    // the exported sheet below has always written it -- the two disagreed until now.
    { key: 'empty_weight',  label: 'Empty Weight(kgs.)',                   text: (i) => i.empty_weight },
    { key: 'top_dia',       label: 'Top Dia(cms.)',                        text: (i) => i.top_dia },
    { key: 'total_height',  label: 'Total Height(cms.)',                   text: (i) => i.total_height },
    { key: 'total_volume',  label: 'Total Volume(ltrs.)',                  text: (i) => i.total_volume },
    { key: 'used_for',      label: 'Notes',             type: 'text',   text: (i) => i.used_for },
    { key: 'status',        label: 'Status',               type: 'status', text: (i) => (Number(i.status) === 1 ? 'Active' : 'Inactive') },
    { key: 'action',        label: 'Action',               filterable: false },
];

function List() {

    const [vesselList, setVesselList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItem, setTotalItem] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);
    // Inline per-column filters, as the Raw Report has them, in place of the single search box
    // that used to sit above the table. { [key]: { op, v1, v2 } } -- see components/Filter.
    const [filters, setFilters] = useState({});

    // role 0 = superadmin, 1 = admin. Both may enable and disable; only the superadmin may delete.
    const role = String(localStorage.getItem('userRole'));
    // Which buttons this user gets is Role Master's call now, not a role number's -- see
    // comman/permissions.js. `role` is still read because it is what the delete and enable
    // endpoints are told to check against.
    const canDisable = can('disable');
    const canActivate = can('active');
    const canDelete = can('delete');

    const navigate = useNavigate();

    useEffect(() => {
        getVessels();
        
    }, []);

    // Every active column filter must pass, matched against the same text the cell shows.
    const activeCols = COLS.filter((c) => c.filterable !== false && isFilterActive(filters[c.key]));
    const visibleRows = vesselList.filter((item) => activeCols.every((c) => matchFilterText(c.text(item), filters[c.key])));
    const onFilterChange = (key, patch) => setFilters((prev) => patchFilter(prev, key, patch));
    const clearFilters = () => setFilters({});



    const getVessels = async () => {
        // try/finally, because the spinner this sets is now on screen: a request that fails --
        // which on a slow connection is the case it exists for -- used to skip the line that
        // turns it off and leave it spinning over an empty table for good.
        setPopupOpen(true);
        try {
            const response = await axios.get(`${baseUrl}/vessel_master/list?page=${page}&limit=${limit}`);
            setVesselList(response.data.data);
            setTotalItem(response.data.totalItem);
        } catch (error) {
            console.log(error);
            alert('Could not load the list. Check the connection and try again.');
        } finally {
            setPopupOpen(false);
        }
    }


    // const deleteVessel = async (id) => {
    //     const shouldDelete = window.confirm("Are you sure you want to disable this ingredient?");
    //     if (shouldDelete) {
    //         try {
    //             await axios.delete(`${baseUrl}/ingredients/delete/${id}`);
    //             window.location.reload();
    //         } catch (error) {
    //             console.log(error);
    //         }
    //     }
    // };

    const disableVessel = async (id) => {
        if (!window.confirm("Are you sure you want to disable this vessel?")) return;
        try {
            await axios.delete(`${baseUrl}/vessel_master/delete/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not disable the vessel.');
        }
    };

    const enableVessel = async (id) => {
        if (!window.confirm("Enable this vessel?")) return;
        try {
            await axios.post(`${baseUrl}/vessel_master/enable/${id}`, { role });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not enable the vessel.');
        }
    };

    // Delete removes the row, unlike Disable which is recoverable, so its confirmation says so.
    const destroyVessel = async (id) => {
        if (!window.confirm("Permanently DELETE this vessel? Disable is reversible; this is not.")) return;
        try {
            await axios.delete(`${baseUrl}/vessel_master/destroy/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not delete the vessel.');
        }
    };

    const editVessel = async (id) => {
        navigate(`/vessel_master/edit/${btoa(id)}`, { state: vesselList.filter(item => item.id == id) });
    }

    // .xlsx, not .csv. A CSV is bytes with no declared encoding, so Excel opens it in the local
    // codepage and every Gujarati name arrives as mojibake -- the vessel names are the column
    // people actually read. An xlsx carries its own UTF-8, so they survive the round trip.
    const handleExport = () => {
        // Column order is the spec's, and the same as COLS above. The last column is
        // headed Notes, not "Used For" -- the spec renames that column on every screen, and a
        // sheet exported from this screen naming it differently would be the odd one out.
        const header = ["Vessel Name", "Vessel Category", "Empty Weight(kgs.)", "Top Dia(cms.)", "Total Height(cms.)", "Total Volume(ltrs.)", "Notes", "Status"];
        const rows = visibleRows.map((item) => ([
            item.name,
            item.vessel_category_name ?? '',
            item.empty_weight,
            item.top_dia,
            item.total_height,
            item.total_volume,
            item.used_for,
            Number(item.status) === 1 ? 'Active' : 'Inactive',
        ]));

        // Say so rather than handing over a sheet with nothing under the headings. An empty
        // export is almost never the export being wrong -- it is the screen having no rows to
        // give it, because a filter is narrowing everything out or the list itself came back
        // empty -- and a file that opens blank sends the reader looking in the wrong place.
        if (rows.length === 0) {
            alert('There is nothing to export: no vessel matches what is on screen.');
            return;
        }

        const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);

        // Widths from the longest value in each column, so the sheet is readable when it opens.
        // Bold, borders and page setup are Pro features of xlsx and never reach the file.
        const columnWidth = (index) => {
            const cells = [header[index], ...rows.map((row) => row[index])];
            const longest = cells.reduce((max, cell) => Math.max(max, String(cell ?? '').length), 0);
            return { wch: Math.min(Math.max(longest + 2, 10), 40) };
        };
        worksheet['!cols'] = header.map((_, index) => columnWidth(index));
        worksheet['!rows'] = [{ hpt: 20 }];
        worksheet['!margins'] = { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 };

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Vessel Master");
        XLSX.writeFile(workbook, "Vessel-Master.xlsx");
    }
   


    return (
        <div className="p-4">
            {popupOpen && <Loader />}
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Vessel Master</h1>
                <div>
                    <Link to="/vessel_master/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Vessel</button>
                    </Link>
                    <button
                    onClick={handleExport}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                >
                    Export
                </button>
                </div>
               
            </div>
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                {/* The search box that used to sit here is gone: the filters are in the header
                    row now, one per column, so a search is made where the column is read. */}
                <div className="flex items-center justify-end pb-4">
                    <button
                        onClick={clearFilters}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                    >
                        Clear
                    </button>
                </div>
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            {COLS.map((c) => <th key={c.key} scope="col" className="px-6 py-3">{c.label}</th>)}
                        </tr>
                        <ColumnFilterRow
                            cols={COLS}
                            filters={filters}
                            onChange={onFilterChange}
                            thClassName="px-2 py-1 bg-gray-50 align-top"
                        />
                    </thead>
                    <tbody>
                    {visibleRows.map((item) => (
                        <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                            <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item.name}</th>
                            <td className="px-6 py-4">{item.vessel_category_name}</td>
                            <td className="px-6 py-4">{item.empty_weight}</td>
                            <td className="px-6 py-4">{item.top_dia}</td>
                            <td className="px-6 py-4">{item.total_height}</td>
                            <td className="px-6 py-4">{item.total_volume}</td>
                            <td className="px-6 py-4">{item.used_for}</td>
                            <td className="px-6 py-4">{Number(item.status) === 1 ? 'Active' : 'Inactive'}</td>
                            <td className="px-6 py-4">
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => editVessel(item.id)}
                                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                                    Edit
                                </button>
                                {item.status == 1 && canDisable && <button
                                    type="button"
                                    onClick={() => disableVessel(item.id)}
                                    className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200">
                                    Disable
                                </button>}
                                {item.status == 0 && canActivate && <button
                                    type="button"
                                    onClick={() => enableVessel(item.id)}
                                    className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring focus:ring-green-200">
                                    Enable
                                </button>}
                                {canDelete && <button
                                    type="button"
                                    onClick={() => destroyVessel(item.id)}
                                    className="px-3 py-1 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring focus:ring-red-300">
                                    Delete
                                </button>}
                            </div>
                            </td>
                        </tr>))}
           
                    </tbody>
                </table>
            </div>
        </div>
  );
}

export default List;